import React, { useState } from 'react';
import { getRiskBadgeClass, getStatusBadgeClass, getStatusText, formatNumber } from '../../utils/reportHelpers';

function ReportTable({ 
  data, 
  columns, 
  onRowClick, 
  pageSize = 10,
  sortable = true,
  showPagination = true 
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Sorting
  const handleSort = (key) => {
    if (!sortable) return;
    
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = sortedData.slice(startIndex, startIndex + pageSize);

  // Generate smart pagination with ellipsis
  const getPageNumbers = () => {
    const delta = 2; // Number of pages to show before and after current page
    const range = [];
    const rangeWithDots = [];
    let l;

    // Always include first page, last page, and pages around current page
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    // Add ellipsis where there are gaps
    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  const pageNumbers = getPageNumbers();

  const renderCellValue = (row, column) => {
    const value = row[column.key];

    if (column.render) {
      return column.render(value, row);
    }

    if (column.key === 'riskLevel') {
      const badges = { high: 'Cao', medium: 'Trung bình', low: 'Thấp', none: 'Không' };
      return <span className={`badge bg-${getRiskBadgeClass(value)}`}>{badges[value] || value}</span>;
    }

    if (column.key === 'status') {
      return <span className={`badge bg-${getStatusBadgeClass(value)}`}>{getStatusText(value)}</span>;
    }

    if (column.type === 'number') {
      return formatNumber(value);
    }

    if (column.type === 'date') {
      return value ? new Date(value).toLocaleDateString('vi-VN') : '-';
    }

    return value || '-';
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                {columns.map(column => (
                  <th 
                    key={column.key}
                    onClick={() => sortable && column.sortable !== false && handleSort(column.key)}
                    style={{ 
                      cursor: sortable && column.sortable !== false ? 'pointer' : 'default',
                      width: column.width || 'auto'
                    }}
                  >
                    {column.label}
                    {sortable && sortConfig.key === column.key && (
                      <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center text-muted py-4">
                    Không có dữ liệu
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, index) => (
                  <tr 
                    key={index}
                    onClick={() => onRowClick && onRowClick(row)}
                    style={onRowClick ? { cursor: 'pointer' } : {}}
                  >
                    {columns.map(column => (
                      <td key={column.key}>
                        {renderCellValue(row, column)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showPagination && totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted">
              Hiển thị {startIndex + 1} - {Math.min(startIndex + pageSize, sortedData.length)} trong tổng số {sortedData.length}
            </div>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Trước
                  </button>
                </li>
                {pageNumbers.map((page, index) => {
                  if (page === '...') {
                    return (
                      <li key={`ellipsis-${index}`} className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                  return (
                    <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Sau
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReportTable;
